import { ProductCategoryType } from '@bcube/shared';
import { Pencil, Trash2 } from 'lucide-react';
import type { PaginatedResponse } from '@/api/commercial/products';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { IProductCategory } from '@/types/commercial/product';

interface ProductCategoryTableProps {
  data: PaginatedResponse<IProductCategory>;
  currentPage: number;
  onPageChange: (page: number) => void;
  onEdit?: (category: IProductCategory) => void;
  onDelete?: (category: IProductCategory) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

export function ProductCategoryTable({
  data,
  currentPage,
  onPageChange,
  onEdit,
  onDelete,
  canEdit = true,
  canDelete = true,
}: ProductCategoryTableProps) {
  const formatDateTime = (value?: string | Date): string => {
    if (!value) return '-';
    const date = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const docs = data.docs ?? [];

  return (
    <div className="w-full space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>CÓDIGO</TableHead>
              <TableHead>NOME</TableHead>
              <TableHead>TIPO</TableHead>
              <TableHead>DATA DE CRIAÇÃO</TableHead>
              <TableHead className="text-right">AÇÕES</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                  Nenhuma categoria encontrada
                </TableCell>
              </TableRow>
            ) : (
              docs.map((category) => {
                const isSystem = category.type === ProductCategoryType.SYSTEM;
                return (
                  <TableRow key={category.id}>
                    <TableCell>
                      <span className="font-medium text-sm">{category.code}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{category.name}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={isSystem ? 'info-subtle' : 'secondary-subtle'}>
                        {isSystem ? 'Sistema' : 'Personalizada'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{formatDateTime(category.created_at)}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1 min-h-[32px] items-center">
                        {canEdit && !isSystem && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            title="Editar"
                            onClick={() => onEdit?.(category)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete && !isSystem && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded bg-white px-2 py-1 text-xs font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-red-50"
                            title="Excluir"
                            onClick={() => onDelete?.(category)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={data.totalPages}
        totalItems={data.total}
        limit={data.limit}
        onPageChange={onPageChange}
      />
    </div>
  );
}
